import { Outlet } from 'react-router-dom';

import { useUserInfo } from '@/hooks';
import { LoaderBackdrop } from '@/components';
import NoCountryAccountScreen from '@/components/country/NoCountryAccountScreen';
import {
  BUSINESS_ROLE_GROUPS,
  userHasPermission,
} from '@/components/security/AppRoles';
import { UserStatus } from '@/pages/Business/pages/Users/enums';
import { useGetBusinessUserMeQuery } from '@/pages/Business/pages/Users/services';

const InactiveAccountScreen = () => (
  <div className='flex min-h-screen w-full items-center justify-center bg-gray-50 p-6'>
    <div className='max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm'>
      <h1 className='mb-3 text-xl font-semibold text-gray-800'>
        Cuenta no disponible
      </h1>
      <p className='text-sm text-gray-600'>
        Tu cuenta está inactiva o no se encuentra habilitada. Contactá al
        administrador para recuperar el acceso.
      </p>
    </div>
  </div>
);

/**
 * El servicio de auth es compartido entre Uruguay y Peru, pero la base de
 * usuarios es de cada pais. Un 404 del whoami significa "el token vale, pero
 * este usuario no existe en ESTE pais" —el caso de alguien de Uruguay que abre
 * el panel de Peru—, y es distinto de que el backend este caido.
 *
 * Solo el 404 dispara la pantalla de pais: cualquier otro fallo (500, sin red)
 * no permite afirmar que la cuenta no exista.
 */
const isNotInThisCountry = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { status?: number }).status === 404;

const AccountStatusGuard = () => {
  const { permissions, email } = useUserInfo();
  const isBusinessUser = userHasPermission(
    permissions,
    BUSINESS_ROLE_GROUPS.full
  );

  const { data, isLoading, error } = useGetBusinessUserMeQuery(undefined, {
    skip: !isBusinessUser,
  });

  if (isBusinessUser && isLoading) return <LoaderBackdrop />;

  if (isNotInThisCountry(error)) {
    return <NoCountryAccountScreen email={email} />;
  }

  const isInactive =
    !!data && (data.active === false || data.status !== UserStatus.ACTIVE);

  if (isInactive) return <InactiveAccountScreen />;

  return <Outlet />;
};

export default AccountStatusGuard;
